// src/pages/YTCampaignDownload.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL;
const YT_API_KEY = process.env.REACT_APP_YT_API_KEY;

const YTCampaignDownload = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null); // full campaign object
  const [progress, setProgress] = useState(null);                 // campaign_status response
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ── On mount: pre-select campaign if navigated from List page ──
  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    const preId = searchParams.get('id');
    if (preId && campaigns.length > 0) {
      setSelectedCampaignId(preId);
    }
  }, [campaigns, searchParams]);

  useEffect(() => {
    if (selectedCampaignId && campaigns.length > 0) {
      const found = campaigns.find(c => c.id === selectedCampaignId);
      setSelectedCampaign(found || null);
      fetchProgress(selectedCampaignId);
    } else {
      setSelectedCampaign(null);
      setProgress(null);
    }
  }, [selectedCampaignId, campaigns]);

  // ── Fetch campaign list ──
  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const response = await axios.get(`${BASE_URL}/youtube_scrapper/list_campaign/`, {
        headers: { 'X-API-KEY': YT_API_KEY },
      });
      if (response.data.status === 'success') {
        setCampaigns(response.data.data || []);
      } else {
        toast.error('Unable to load campaigns. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Access denied. Please contact your administrator.');
      } else if (status >= 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Unable to load campaigns. Please try refreshing the page.');
      }
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // ── Fetch campaign progress / status ──
  const fetchProgress = async (campaignId) => {
    setLoadingProgress(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/youtube_scrapper/${campaignId}/campaign_status/`,
        { headers: { 'X-API-KEY': YT_API_KEY } }
      );
      if (response.data.status === 'success') {
        setProgress(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  // ── Download CSV ──
  const handleDownload = async () => {
    if (!selectedCampaignId) return;
    setDownloading(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/youtube_scrapper/${selectedCampaignId}/campaign_download/`,
        {
          headers: { 'X-API-KEY': YT_API_KEY },
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Filename from campaign name + date
      const safeName = (selectedCampaign?.name || 'campaign').replace(/[^a-z0-9]/gi, '_');
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `${safeName}_${date}.csv`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('CSV downloaded successfully!');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      const status = error.response?.status;
      if (status === 404) {
        toast.error('No data found for this campaign yet. Videos may still be getting fetched.');
      } else if (status === 401 || status === 403) {
        toast.error('Access denied. Please contact your administrator.');
      } else if (status >= 500) {
        toast.error('Server error while downloading. Please try again in a moment.');
      } else if (!status) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Download failed. Please try again or contact support.');
      }
    } finally {
      setDownloading(false);
    }
  };

  // ── Progress percentage ──
  const pct = Math.min(parseFloat(progress?.progress_percentage) || 0, 100);
  const isCompleted = selectedCampaign?.status === 'inactive' && pct >= 100;
  const isInProgress = selectedCampaign?.status === 'active';

  // ── Format number ──
  const fmt = (n) => (n || 0).toLocaleString();

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2500,
          style: { background: '#363636', color: '#fff', borderRadius: '8px' },
        }}
      />

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col items-start gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Download Campaign CSV</h1>
            <p className="text-sm text-gray-600 mt-1">
              Select a campaign to download its video data as a CSV file
            </p>
          </div>
          <button
            onClick={() => navigate('/yt/campaign/list')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            ← Back to Campaign List
          </button>
        </div>
      </div>

      {/* Campaign Selection */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Campaign
        </label>
        <select
          value={selectedCampaignId}
          onChange={(e) => setSelectedCampaignId(e.target.value)}
          disabled={loadingCampaigns}
          className="w-full md:w-96 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">
            {loadingCampaigns ? 'Loading campaigns...' : '-- Select a Campaign --'}
          </option>
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name}
            </option>
          ))}
        </select>

        {campaigns.length === 0 && !loadingCampaigns && (
          <p className="mt-2 text-sm text-gray-500 italic">
            No campaigns available. Create a campaign first.
          </p>
        )}
      </div>

      {/* Campaign Info + Download */}
      {selectedCampaignId && (
        <div className="bg-white rounded-lg shadow">
          {/* Section Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedCampaign?.name || '...'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Campaign status and download options
            </p>
          </div>

          {/* Progress Loading */}
          {loadingProgress && (
            <div className="px-6 py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2" />
              <p className="text-gray-500 text-sm">Loading campaign info...</p>
            </div>
          )}

          {/* Campaign Info */}
          {!loadingProgress && progress && (
            <div className="px-6 py-6 space-y-6">
              {/* In-progress warning */}
              {isInProgress && (
                <div className="flex gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Campaign still in progress</p>
                    <p className="text-xs text-yellow-700 mt-0.5">
                      {pct.toFixed(1)}% complete — You can download now but the CSV will be partial.
                    </p>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Videos Fetched</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {fmt(progress.total_videos_fetched)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Target</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {fmt(progress.target_videos)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Progress</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {pct.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    selectedCampaign?.status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {selectedCampaign?.status || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>0</span>
                  <span>{fmt(progress.target_videos)} target</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${isCompleted ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>



              {/* Download Button */}
              <div className="pt-2">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition-colors font-medium"
                >
                  {downloading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      {isInProgress ? 'Download Partial CSV' : 'Download CSV'}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400 mt-2">
                  File will be saved as: {(selectedCampaign?.name || 'campaign').replace(/[^a-z0-9]/gi, '_')}_{new Date().toISOString().split('T')[0]}.csv
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions — shown when no campaign selected */}
      {!selectedCampaignId && !loadingCampaigns && campaigns.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex gap-3">
            <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-medium text-blue-900 mb-2">How to Download</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Select a campaign from the dropdown above</li>
                <li>Review the campaign progress and video count</li>
                <li>Click "Download CSV" to save the file to your computer</li>
                <li>CSV includes: video ID, URL, title, channel, views, duration, language, keyword, and more</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YTCampaignDownload;